import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft,
  ChevronRight,
  Clock,
  ThumbsUp,
  Share2,
  CheckCircle2,
  Calendar,
  MousePointerClick,
  Edit3,
  Trash2,
  MessageCircle,
  Eye,
  AlertTriangle,
  Link2,
  RefreshCw,
  Smartphone,
  Unlink
} from "lucide-react";

export default function AgendaTutorialPage() {
  const { user } = useAuth();
  
  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans pb-20">
      <header className="border-b bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/">
            <img src="/marketing/logo_official.png" className="h-28 object-contain" alt="OdontoAssist AI Logo" />
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/ajuda/tutoriais" className="text-gray-500 hover:text-indigo-600 font-semibold hidden md:block">
              Central de Guias
            </Link>
            <Link 
              to="/" 
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl font-black tracking-wide flex items-center gap-2 transition-all shadow-lg hover:-translate-y-1"
            >
              <ArrowLeft size={20} />
              Voltar {user ? "para o Aplicativo" : "ao Início"}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 mb-6">
          <Link to="/ajuda" className="hover:text-indigo-600">Ajuda</Link>
          <ChevronRight size={14} className="text-gray-400" />
          <Link to="/ajuda/tutoriais" className="hover:text-indigo-600">Guias</Link>
          <ChevronRight size={14} className="text-gray-400" />
          <span className="text-indigo-600">Gestão e Agenda</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
          Agendamento Inteligente
        </h1>
        
        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-8">
          <div className="flex items-center gap-2">
            <Clock size={16} /> <span>12 min de conteúdo dividido em 3 vídeos</span>
          </div>
          <div className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-bold uppercase tracking-wider text-xs">
            Gestão e Agenda
          </div>
        </div>

        <div className="space-y-10 mb-12">

          {/* Vídeo 1 */}
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-4">
              1. Configuração da Agenda e Autoagendamento
            </h2>

            <div className="w-full aspect-video rounded-3xl overflow-hidden relative shadow-2xl border-4 border-white bg-black">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/tpFAnZkUMPU"
                title="Configuração da Agenda e Autoagendamento"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full"
              ></iframe>
            </div>
          </div>


          {/* Vídeo 2 */}
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-4">
              2. Utilizando a Agenda no Dia a Dia
            </h2>

            <div className="w-full aspect-video rounded-3xl overflow-hidden relative shadow-2xl border-4 border-white bg-black">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/pPO7IXk2pDA"
                title="Utilizando a Agenda no Dia a Dia"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full"
              ></iframe>
            </div>
          </div>


          {/* Vídeo 3 */}
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-4">
              3. Utilizando o Autoagendamento
            </h2>

            <div className="w-full aspect-video rounded-3xl overflow-hidden relative shadow-2xl border-4 border-white bg-black">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/Lz4bGFMpGzs"
                title="Utilizando o Autoagendamento"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full"
              ></iframe>
            </div>
          </div>

        </div>

        {/* Written Tutorial */}
        <article className="prose prose-indigo prose-lg max-w-none text-gray-600 text-justify">
          <p className="lead text-xl text-gray-700 font-medium">
            A Agenda Inteligente do OdontoAssist AI centraliza o agendamento clínico, bloqueios de horários, múltiplos profissionais, autoagendamento online e integração com o Google Calendar em um único fluxo.

            Com ela, sua clínica pode organizar atendimentos internos, permitir marcações feitas pelo próprio paciente e acompanhar toda a jornada clínica com segurança e produtividade.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Passo a Passo em Texto</h2>
          
          <div className="space-y-8 mt-8">
            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">1</span>
                 Configuração Inicial da Agenda
               </h3>
                
                <p>
                Antes de utilizar a agenda no dia a dia, recomendamos iniciar pela aba <strong>Configurações → Horários</strong>.
                </p>

                <p>
                Nessa tela você poderá definir horários de atendimento, duração padrão das consultas, profissionais disponíveis, intervalos internos como almoço e regras do autoagendamento online.
                </p>                

                <p>
                  Ao abrir a página da <strong>Agenda</strong>, você verá um painel resumo na parte superior da tela.
                </p>

                <p>
                  Esse dashboard mostra automaticamente quantos atendimentos você possui <strong>Hoje</strong>, quantos estão programados para a <strong>Semana Atual</strong>, quantos já foram <strong>Confirmados</strong> e quantos ainda seguem como <strong>Pendentes</strong>.
                </p>

                <p>
                  Esses totais são recalculados em tempo real conforme você cria, edita ou altera o status dos blocos de horários no calendário.
                </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">2</span>
                 Operação Diária da Agenda
               </h3>
               <p>Existem duas maneiras rápidas de criar um novo agendamento:</p>
               <ul className="mt-2 list-none space-y-4">
                  <li className="flex items-start gap-3">
                     <MousePointerClick className="shrink-0 text-indigo-500 mt-1" size={20} />
                     <div>
                       <strong>Clique Direto:</strong><br/>
                       Na visualização diária ou semanal, clique em qualquer espaço em branco da grade. O formulário será aberto automaticamente com aquela data e horário já pré-selecionados.
                     </div>
                  </li>
                  <li className="flex items-start gap-3">
                     <Calendar className="shrink-0 text-indigo-500 mt-1" size={20} />
                     <div>
                       <strong>Botão "Novo Agendamento":</strong><br/>
                       Use o botão <strong>Novo Agendamento</strong> no topo da tela. Escolha o paciente, informe o procedimento e classifique o atendimento com etiquetas visuais como <strong>Agendado</strong>, <strong>Confirmado</strong>, <strong>Realizado</strong> ou <strong>Cancelado</strong>.
                     </div>
                  </li>
               </ul>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">3</span>
                 Fluxo Clínico do Atendimento
               </h3>
                <p>
                  Ao clicar em um agendamento já criado na grade, será aberto o pop-up com os <strong>Detalhes do Agendamento</strong>.
                </p>

                <p>
                  Essa tela oferece atalhos importantes para a produtividade da recepção e do profissional.
                </p>
               <div className="grid md:grid-cols-2 gap-4 mt-4">
                 <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <h4 className="font-bold flex items-center gap-2 text-blue-700 mb-2"><Edit3 size={18} /> Modificar e Status</h4>
                    <p className="text-sm text-gray-600">Altere notas, edite os horários ou atualize rapidamente a tag de "Pendente" para "Confirmado" se o paciente entrou em contato informando que comparecerá.</p>
                 </div>  
                 <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <h4 className="font-bold flex items-center gap-2 text-emerald-700 mb-2">Check-in - Entrar no Prontuário</h4>
                    <p className="text-sm text-gray-600">Leva você diretamente para a ficha clínica do paciente em atendimento. A partir dali, você pode navegar pelo prontuário, consultar histórico, acessar imagens, atualizar odontogramas e utilizar qualquer funcionalidade clínica necessária.</p>
                 </div>
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <h4 className="font-bold flex items-center gap-2 text-purple-700 mb-2">
                      Evolução Clínica
                    </h4>
                    <p className="text-sm text-gray-600">
                      Após navegar nas abas do prontuário, volte para a aba de agendamentos, para registrar a evolução clínica daquele atendimento, vincular opcionalmente a um tratamento e até criar lembretes de retorno para o paciente.
                    </p>
                  </div> 
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <h4 className="font-bold flex items-center gap-2 text-emerald-700 mb-2">Finalizar Atendimento</h4>
                    <p className="text-sm text-gray-600">Para finalizar é importante salvar a evolução e clicar em Concluído, no lado superior direito.</p>
                  </div>    
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <h4 className="font-bold flex items-center gap-2 text-green-700 mb-2">
                      WhatsApp e Notificações
                    </h4>

                    <p className="text-sm text-gray-600">
                      Quando o módulo WhatsApp Business estiver ativo, você poderá visualizar o histórico de notificações enviadas ao paciente e reenviar confirmações manualmente sempre que necessário.
                    </p>
                  </div>                                                               
               </div>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
               <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
               <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                 <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">4</span>
                 Autoagendamento do Paciente
               </h3>
                <p>
                Após configurar o link público da clínica, seus pacientes poderão realizar agendamentos online.
                </p>

                <p>
                O paciente passa por um fluxo seguro com:
                </p>

                <ul>
                  <li>identificação por CPF;</li>
                  <li>confirmação por código enviado por e-mail;</li>
                  <li>seleção do profissional;</li>
                  <li>escolha entre horários realmente disponíveis.</li>
                </ul>

                <p>
                Depois do agendamento, o paciente poderá receber links seguros para cancelamento ou remarcação, conforme as regras definidas pela clínica.
                </p>
            </div>
          </div>

          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 left-0 w-2 h-full bg-green-500" />

            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-black">5</span>
              Comunicação Automática com o Paciente
            </h3>

            <p>
              Quando o módulo <strong>WhatsApp Business Integrado</strong> está ativo, a agenda passa a se conectar diretamente com a comunicação da clínica. Isso permite enviar mensagens ao paciente sem depender do WhatsApp Web aberto no computador.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 my-5">
              <p className="text-sm text-amber-800 mb-0">
                <strong>Atenção:</strong> este recurso é diferente da Extensão WhatsApp Web. O WhatsApp Business Integrado usa a API oficial da Meta e funciona dentro do OdontoAssist AI. A Extensão WhatsApp Web é uma ferramenta separada para abrir o WhatsApp Web comum no navegador.
              </p>
            </div>

            <p>
              Pela agenda, a clínica pode utilizar mensagens relacionadas ao atendimento, como:
            </p>

            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="bg-green-50 p-4 rounded-xl border border-green-100">
                <h4 className="font-bold flex items-center gap-2 text-green-700 mb-2">
                  <MessageCircle size={18} />
                  Confirmação de Consulta
                </h4>
                <p className="text-sm text-gray-600">
                  Envie ao paciente uma confirmação com clínica, endereço, data, horário, profissional e contatos oficiais.
                </p>
              </div>

              <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                <h4 className="font-bold flex items-center gap-2 text-blue-700 mb-2">
                  <Clock size={18} />
                  Lembretes
                </h4>
                <p className="text-sm text-gray-600">
                  Configure lembretes para reduzir esquecimentos e ajudar o paciente a se organizar antes da consulta.
                </p>
              </div>

              <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
                <h4 className="font-bold flex items-center gap-2 text-purple-700 mb-2">
                  <Calendar size={18} />
                  Remarcações
                </h4>
                <p className="text-sm text-gray-600">
                  Quando uma consulta é remarcada, o paciente pode receber uma mensagem informando a nova data e horário.
                </p>
              </div>

              <div className="bg-red-50 p-4 rounded-xl border border-red-100">
                <h4 className="font-bold flex items-center gap-2 text-red-700 mb-2">
                  <Trash2 size={18} />
                  Cancelamentos
                </h4>
                <p className="text-sm text-gray-600">
                  Caso a consulta seja cancelada, a clínica pode comunicar o paciente por mensagem padronizada.
                </p>
              </div>
            </div>

            <h4 className="text-lg font-bold text-gray-900 mt-6 mb-2">
              Histórico de notificações
            </h4>

            <p>
              No detalhe do agendamento, o OdontoAssist registra o histórico de notificações enviadas. Assim, a equipe consegue saber se uma mensagem foi enviada, entregue ou lida pelo paciente.
            </p>

            <div className="mt-4 bg-green-50 border border-green-200 rounded-2xl p-4">
              <strong>Exemplo:</strong>
              <br />
              WhatsApp de confirmação enviado em 05/07/2026 às 09:50
              <br />
              WhatsApp lido em 05/07/2026 às 09:51
            </div>

            <h4 className="text-lg font-bold text-gray-900 mt-6 mb-2 flex items-center gap-2">
              <Eye size={20} className="text-indigo-600" />
              Ver mensagem enviada
            </h4>

            <p>
              Além do status, a clínica pode visualizar o conteúdo da mensagem enviada. Isso ajuda a conferir exatamente quais dados foram enviados ao paciente, como endereço, horário, profissional e contatos da clínica.
            </p>

            <h4 className="text-lg font-bold text-gray-900 mt-6 mb-2 flex items-center gap-2">
              <MessageCircle size={20} className="text-green-600" />
              Respostas do paciente
            </h4>

            <p>
              Quando o paciente responde a uma mensagem enviada pela agenda, a conversa pode aparecer automaticamente na página <strong>WhatsApp Business</strong>. A partir dali, a equipe pode acompanhar a conversa e responder enquanto a janela de atendimento estiver aberta.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mt-5">
              <h4 className="font-bold text-amber-900 flex items-center gap-2 mb-2">
                <AlertTriangle size={18} />
                Janela de atendimento de 24 horas
              </h4>
              <p className="text-sm text-amber-800 mb-0">
                Após uma mensagem do paciente, o WhatsApp permite respostas livres por até 24 horas. Depois desse prazo, a janela é encerrada automaticamente. Essa é uma regra da API oficial da Meta, não uma limitação do OdontoAssist AI.
              </p>
            </div>

            <p className="mt-5">
              Para aprender todos os detalhes do módulo, consulte também o tutorial específico <strong>WhatsApp Business Integrado</strong> na Central de Guias.
            </p>
          </div>

          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden mt-8">
            <div className="absolute top-0 left-0 w-2 h-full bg-blue-500" />

            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-black">6</span>
              Integração com o Google Calendar
            </h3>

            <p>
              Quando essa funcionalidade estiver disponível para sua conta, cada profissional poderá conectar sua própria conta Google e visualizar no Google Calendar os compromissos profissionais criados no OdontoAssist AI junto com seus compromissos pessoais.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 my-5">
              <h4 className="font-bold text-blue-900 flex items-center gap-2 mb-3">
                <Link2 size={19} /> Como conectar
              </h4>
              <ol className="list-decimal pl-6 space-y-2 text-sm text-blue-900">
                <li>Acesse <strong>Configurações → Integrações</strong>.</li>
                <li>Localize o profissional e clique em <strong>Conectar Google Calendar</strong>.</li>
                <li>Escolha a conta Google utilizada pelo profissional.</li>
                <li>Avance pelas telas apresentadas pelo Google.</li>
                <li>Selecione a permissão <strong>“Ver, criar, mudar e excluir eventos nas suas agendas do Google”</strong>.</li>
                <li>Clique em <strong>Continuar</strong>. Ao concluir, você retornará automaticamente ao OdontoAssist AI.</li>
              </ol>
              <p className="text-xs text-blue-800 mt-4 mb-0">
                Durante uma fase de testes controlados, o Google poderá informar que o aplicativo está em teste. Usuários convidados podem clicar em <strong>Continuar</strong> após confirmar que o aplicativo exibido é o OdontoAssist AI.
              </p>
            </div>

            <h4 className="text-lg font-bold text-gray-900 mt-6 mb-3">
              O que é sincronizado
            </h4>

            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-green-600 shrink-0 mt-1" size={19} />
                <span>Agendamentos futuros já existentes no momento da conexão e novos agendamentos criados pela Agenda, pelo Prontuário ou pelo autoagendamento.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-green-600 shrink-0 mt-1" size={19} />
                <span>Alterações de data, horário, duração, procedimento e profissional.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-green-600 shrink-0 mt-1" size={19} />
                <span>Cancelamentos, remarcações e exclusões, inclusive quando realizados pelos links públicos disponibilizados ao paciente.</span>
              </li>
            </ul>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 my-5">
              <p className="text-sm text-amber-900 mb-0">
                <strong>Importante:</strong> a sincronização é unidirecional, do OdontoAssist AI para o Google Calendar. Compromissos pessoais criados no Google não são importados para o OdontoAssist e não bloqueiam automaticamente os horários da agenda clínica.
              </p>
            </div>

            <p>
              Para preservar a privacidade do paciente, o evento utiliza uma identificação reduzida, com o primeiro nome e a inicial do último sobrenome. Também são enviados data, horário, duração, procedimento e profissional. A senha da Conta Google não é acessada nem armazenada pelo OdontoAssist AI.
            </p>

            <div className="grid md:grid-cols-2 gap-4 mt-5">
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                <h4 className="font-bold flex items-center gap-2 text-purple-700 mb-2">
                  <RefreshCw size={18} /> Sincronizar agora
                </h4>
                <p className="text-sm text-gray-600">
                  Use esse botão para fazer uma nova conferência dos agendamentos futuros. Se houver falha de autorização, a tela indicará quando será necessário reconectar a conta.
                </p>
              </div>

              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                <h4 className="font-bold flex items-center gap-2 text-red-700 mb-2">
                  <Unlink size={18} /> Desconectar
                </h4>
                <p className="text-sm text-gray-600">
                  Ao desconectar, escolha entre manter no Google os eventos já criados ou solicitar sua remoção antes de encerrar a integração.
                </p>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mt-5">
              <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                <Smartphone size={19} className="text-gray-700" /> Também funciona no iPhone
              </h4>
              <p className="text-sm text-gray-700 mb-0">
                Instale o aplicativo <strong>Google Agenda</strong> e acesse a mesma conta conectada ao OdontoAssist. Se preferir o aplicativo <strong>Calendário</strong> da Apple, adicione a conta em <strong>Ajustes → Apps → Calendário → Contas de Calendário → Adicionar Conta → Google</strong> e mantenha a opção <strong>Calendários</strong> ativada. A integração não cria eventos diretamente em uma agenda exclusiva do iCloud.
              </p>
            </div>
          </div>

          <div className="bg-amber-50 p-8 rounded-3xl border border-amber-200 shadow-sm relative overflow-hidden mt-8">
            <div className="absolute top-0 left-0 w-2 h-full bg-amber-500" />
            <h3 className="text-xl font-bold text-amber-950 mb-3 flex items-center gap-3">
              <AlertTriangle className="text-amber-600" size={24} />
              Agendamentos internos no mesmo horário
            </h3>
            <p className="text-amber-950">
              Se um usuário da equipe tentar criar ou remarcar um atendimento para um horário já ocupado pelo mesmo profissional, o OdontoAssist exibirá os agendamentos conflitantes antes de salvar.
            </p>
            <ol className="text-amber-950">
              <li>Confira os pacientes, horários e procedimentos apresentados no aviso.</li>
              <li>Escolha <strong>Cancelar</strong> para voltar ao formulário ou <strong>Agendar mesmo assim</strong> para confirmar a sobreposição.</li>
              <li>Os eventos sobrepostos ficam identificados na agenda com destaque, ícone e indicação de conflito.</li>
            </ol>
            <div className="bg-white/80 border border-amber-300 rounded-2xl p-4 mt-5 text-sm text-amber-950">
              <strong>Importante:</strong> essa confirmação existe apenas para agendamentos feitos por usuários internos autorizados. O autoagendamento por link continua mostrando somente horários disponíveis e nunca permite ao paciente forçar um conflito. Bloqueios de horário também continuam sendo respeitados.
            </div>
            <p className="text-sm text-amber-900 mt-4 mb-0">
              A confirmação fica registrada na auditoria da clínica. Use a sobreposição apenas para situações deliberadas, como encaixes e avaliações rápidas, depois de conferir a capacidade da clínica e do profissional.
            </p>
          </div>

          <div className="bg-indigo-900 rounded-3xl p-8 mt-12 text-white">
             <h3 className="text-2xl font-bold mb-4">Recursos Avançados da Agenda</h3>
             <ul className="space-y-3">
               <li className="flex items-start gap-3">
                 <CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} />
                 <span>Você pode usar os filtros internos do calendário para trocar a exibição entre <strong>Mês</strong> (visão global), <strong>Semana</strong> (visão padrão) ou <strong>Dia</strong> (visão horária super detalhada daquele dia específico).</span>
               </li>

                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} />
                  <span>Autoagendamento online para pacientes.</span>
                </li>

                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} />
                  <span>Cancelamento e remarcação seguros através de links enviados por e-mail.</span>
                </li>

                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} />
                  <span>Confirmações, lembretes, remarcações e cancelamentos por e-mail e WhatsApp Business Integrado.</span>
                </li>

                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} />
                  <span>Histórico completo de notificações enviadas aos pacientes.</span>
                </li>

                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} />
                  <span>Visualização do conteúdo enviado e status como enviada, entregue e lida.</span>
                </li>

                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} />
                  <span>Integração com a Central WhatsApp Business para acompanhar respostas dos pacientes.</span>
                </li>

                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} />
                  <span>Integração direta com prontuário, evolução clínica e odontograma.</span>
                </li>

                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} />
                  <span>Integração opcional com o Google Calendar, individual por profissional, com atualização automática de agendamentos, alterações e cancelamentos.</span>
                </li>               
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-indigo-400 shrink-0 mt-1" size={20} />
                  <span>Alerta e confirmação consciente quando a equipe interna precisa manter dois agendamentos do mesmo profissional no mesmo horário.</span>
                </li>
             </ul>
          </div>
        </article>
      </main>
    </div>
  );
}
